import { useEffect, useState } from "react";
import { Plus, Trash2, X, Leaf } from "lucide-react";
import { toast } from "../../hooks/use-toast";
import ConfirmAlert from "../../components/ui/ConfirmAlert";
import api from "../../services/api";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ---------------- LOAD USERS ---------------- */
  const loadUsers = async () => {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  /* ---------------- ADD USERS ---------------- */
  const handleAddSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post("/register", {
        email: newUser.email,
        password: newUser.password,
        role: "admin",
      });

      toast({
        title: "User Added",
        description: `Admin user created successfully`,
      });

      loadUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to add user",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setShowAddForm(false);
      setNewUser({ email: "", password: "" });
    }
  };

  /* ---------------- DELETE USER ---------------- */
  const requestDelete = (id) => {
    setSelectedUserId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUserId) return;

    try {
      await api.post(`/users/${selectedUserId}`, {
        _method: "DELETE"
      });

      toast({
        title: "User Deleted",
        description: "User removed successfully",
      });

      loadUsers();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.message || "Failed to delete user",
        variant: "destructive",
        duration: 5000,
      });
    } finally {
      setConfirmOpen(false);
      setSelectedUserId(null);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif font-bold text-foreground mb-2">
            Users Management
          </h2>
          <p className="text-muted-foreground">Add Admins to work with you</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="btn-hero flex items-center gap-2 px-5 py-3 text-sm"
        >
          <Plus size={18} />
          <span>Add User</span>
        </button>
      </div>

      {/* ---------------- ADD MODAL ---------------- */}
      {showAddForm && (
        <div className="fixed inset-0 bg-foreground/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl shadow-large max-w-md w-full">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h3 className="text-xl font-serif font-semibold">Add New User</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleAddSubmit} className="p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) =>
                    setNewUser({ ...newUser, email: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Password
                </label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) =>
                    setNewUser({ ...newUser, password: e.target.value })
                  }
                  required
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all outline-none"
                />
              </div>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="flex-1 px-4 py-3 rounded-xl border border-border"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-hero">
                  Add User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading & Error */}
      {loading && <p className="text-center">Loading users...</p>}
      {error && <p className="text-center text-red-500">{error}</p>}

      {/* Empty State */}
      {!loading && !error && users.length === 0 && (
        <div className="text-center py-16">
          <Leaf className="w-10 h-10 mx-auto text-muted-foreground mb-4" />
          <p className="text-lg text-muted-foreground">
            No users available.
          </p>
        </div>
      )}

      {/* Users Grid */}
      {!loading && !error && users.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <div
              key={user.id}
              className="bg-card p-6 rounded-2xl shadow-soft flex items-center justify-between"
            >
              <h3 className="text-lg font-semibold text-foreground">
                {user.email}
              </h3>

              <button
                onClick={() => requestDelete(user.id)}
                className="p-2 hover:bg-destructive/10 rounded-lg text-destructive"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ✅ MOVED OUTSIDE GRID (FIXED WARNING) */}
      {confirmOpen && (
        <ConfirmAlert
          open={confirmOpen}
          title="Delete User"
          description="Are you sure you want to delete this user? This action cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminUsers;
